import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import {
  API_BASE_URL,
  REQUEST_TIMEOUT,
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  SESSION_ACCESS_KEY,
  SESSION_REFRESH_KEY,
} from '@/config/constants';
import type {
  AxiosWrapperType,
  ItemResponse,
  ListResponse,
  ListPaginated,
  AuthTokens,
} from '@/types/api';

export interface AxiosClientProps {
  endpoint?: string;
  contentType?:
    | 'multipart/form-data'
    | 'application/json'
    | 'application/octet-stream';
  isToken?: boolean;
  baseURL?: string;
  timeout?: number;
}

class AxiosClient<GenericReq = unknown, GenericRes = unknown> {
  endpoint: string;
  instance: AxiosInstance;
  headers: Record<string, string>;

  constructor({
    endpoint = '',
    isToken = true,
    contentType = 'application/json',
    baseURL = API_BASE_URL,
    timeout = REQUEST_TIMEOUT,
  }: AxiosClientProps) {
    // Create axios instance with base configuration
    const axiosInstance = axios.create({
      baseURL,
      timeout,
      headers: {
        'Content-Type': contentType,
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    // Request Interceptor - Add authentication token and Accept-Language header
    axiosInstance.interceptors.request.use(
      async config => {
        if (isToken) {
          // Try to get token from multiple sources
          const accessToken = this.getAccessToken();

          if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
          }
        }

        // Add Accept-Language header based on current i18n language
        const currentLanguage = this.getCurrentLanguage();
        if (currentLanguage) {
          if (currentLanguage === 'ar') {
            config.headers['Accept-Language'] = 'ar,ar-SA;q=0.9,en;q=0.8';
          } else {
            config.headers['Accept-Language'] = 'en-US,en;q=0.9,ar;q=0.8';
          }
        }

        return config;
      },
      error => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response Interceptor - Handle token refresh and errors
    axiosInstance.interceptors.response.use(
      response => {
        // Successful response - return as is
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        // Handle 500, 503, 504 Server Errors - Dispatch global error event
        if (
          error.response?.status === 500 ||
          error.response?.status === 503 ||
          error.response?.status === 504
        ) {
          const errorData = error.response.data as {
            error?: string;
            detail?: string;
            message?: string;
          };

          // Dispatch custom event for global error handling
          window.dispatchEvent(
            new CustomEvent('server:error', {
              detail: {
                status: error.response.status,
                message: errorData?.error || error.message,
                detail: errorData?.detail || errorData?.message,
                endpoint: error.config?.url,
              },
            })
          );

          return Promise.reject(error);
        }

        // Handle 401 Unauthorized - Clear auth and trigger redirect
        // Note: Laravel Sanctum uses long-lived tokens without refresh endpoints
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          console.warn('401 Unauthorized - Token expired or invalid');

          // Clear stored tokens and dispatch auth:expired event
          // This will trigger AuthProvider to redirect to login
          this.clearAuthData();

          // Return rejected promise to prevent retry
          return Promise.reject(error);
        }

        // Handle other errors
        return Promise.reject(error);
      }
    );

    this.endpoint = endpoint;
    this.instance = axiosInstance;
    this.headers = {};
  }

  /**
   * Get current language from i18n storage
   */
  private getCurrentLanguage(): string | null {
    if (typeof window === 'undefined') return null;

    try {
      // Try to get from localStorage (i18nextLng is the default key used by react-i18next)
      const language = localStorage.getItem('i18nextLng');
      return language || 'en'; // Default to English if not found
    } catch (error) {
      console.warn('Failed to get current language:', error);
      return 'en';
    }
  }

  /**
   * Get access token from storage (multiple fallback sources)
   */
  private getAccessToken(): string | null {
    if (typeof window === 'undefined') return null;

    // 1. Try session storage first (fastest)
    const token = sessionStorage.getItem(SESSION_ACCESS_KEY);
    if (token) return token;

    // 2. Try local storage tokens object
    try {
      const tokensStr = localStorage.getItem(AUTH_TOKEN_KEY);
      if (tokensStr) {
        const tokens: AuthTokens = JSON.parse(tokensStr);
        if (tokens.access) {
          // Update session storage for faster access
          sessionStorage.setItem(SESSION_ACCESS_KEY, tokens.access);
          return tokens.access;
        }
      }
    } catch (error) {
      console.warn('Failed to parse stored tokens:', error);
    }

    return null;
  }

  /**
   * Get refresh token from storage
   */
  private getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;

    // 1. Try session storage first
    const token = sessionStorage.getItem(SESSION_REFRESH_KEY);
    if (token) return token;

    // 2. Try local storage tokens object
    try {
      const tokensStr = localStorage.getItem(AUTH_TOKEN_KEY);
      if (tokensStr) {
        const tokens: AuthTokens = JSON.parse(tokensStr);
        return tokens.refresh || null;
      }
    } catch (error) {
      console.warn('Failed to parse stored tokens:', error);
    }

    return null;
  }

  /**
   * DEPRECATED: Laravel Sanctum uses long-lived tokens without refresh endpoints
   * This method is kept for reference but should not be used
   * Token expiration is handled by 401 interceptor which clears auth and redirects
   */
  // private async refreshToken(): Promise<AuthTokens | null> {
  //   const refreshToken = this.getRefreshToken();
  //
  //   if (!refreshToken) {
  //     throw new Error('No refresh token available');
  //   }
  //
  //   // For Sanctum, we would typically make a request to refresh endpoint
  //   // Since Sanctum doesn't have built-in refresh, this is a placeholder
  //   // You might implement a custom refresh endpoint or use token validation
  //
  //   try {
  //     const response = await axios.post(`${API_BASE_URL}/refresh`, {
  //       refresh_token: refreshToken,
  //     });
  //
  //     if (response.data?.status && response.data?.data?.tokens) {
  //       const newTokens: AuthTokens = response.data.data.tokens;
  //       this.storeTokens(newTokens);
  //       return newTokens;
  //     }
  //   } catch (error) {
  //     console.error('Token refresh request failed:', error);
  //   }
  //
  //   return null;
  // }

  /**
   * Store authentication tokens in multiple storage locations
   */
  private storeTokens(tokens: AuthTokens): void {
    if (typeof window === 'undefined') return;

    try {
      // Store in localStorage for persistence
      localStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(tokens));

      // Store in sessionStorage for quick access
      sessionStorage.setItem(SESSION_ACCESS_KEY, tokens.access);
      sessionStorage.setItem(SESSION_REFRESH_KEY, tokens.refresh);
    } catch (error) {
      console.error('Failed to store tokens:', error);
    }
  }

  /**
   * Clear stored authentication data and dispatch auth expired event
   */
  private clearAuthData(): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
      sessionStorage.removeItem(SESSION_ACCESS_KEY);
      sessionStorage.removeItem(SESSION_REFRESH_KEY);

      // Dispatch custom event to notify AuthProvider
      window.dispatchEvent(new CustomEvent('auth:expired'));
    } catch (error) {
      console.error('Failed to clear auth data:', error);
    }
  }

  /**
   * Combine configuration objects
   */
  private combineConfig(config?: AxiosRequestConfig): AxiosRequestConfig {
    return {
      ...config,
      headers: { ...this.headers, ...config?.headers },
    };
  }

  // ============================================================================
  // API Methods
  // ============================================================================

  /**
   * GET request for single item
   */
  getItem = async (
    config?: AxiosRequestConfig
  ): Promise<ItemResponse<GenericRes>> => {
    try {
      const response = await this.instance.get<
        GenericReq,
        AxiosWrapperType<ItemResponse<GenericRes>>
      >(this.endpoint, this.combineConfig(config));
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return error.response.data;
      }
      throw error;
    }
  };

  /**
   * GET request for list of items (non-paginated)
   */
  getList = async (
    config?: AxiosRequestConfig
  ): Promise<ListResponse<GenericRes>> => {
    try {
      const response = await this.instance.get<
        GenericReq,
        AxiosWrapperType<ListResponse<GenericRes>>
      >(this.endpoint, this.combineConfig(config));
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return error.response.data;
      }
      throw error;
    }
  };

  /**
   * GET request for paginated list of items
   */
  getPaginated = async (
    config?: AxiosRequestConfig
  ): Promise<ListPaginated<GenericRes>> => {
    try {
      const response = await this.instance.get<
        GenericReq,
        AxiosWrapperType<ListPaginated<GenericRes>>
      >(this.endpoint, this.combineConfig(config));
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return error.response.data;
      }
      throw error;
    }
  };

  /**
   * POST request
   */
  post = async (
    body?: GenericReq,
    config?: AxiosRequestConfig
  ): Promise<ItemResponse<GenericRes>> => {
    try {
      const response = await this.instance.post<
        GenericReq,
        AxiosWrapperType<ItemResponse<GenericRes>>
      >(this.endpoint, body, this.combineConfig(config));
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return error.response.data;
      }
      throw error;
    }
  };

  /**
   * PUT request
   */
  put = async (
    body?: GenericReq,
    config?: AxiosRequestConfig
  ): Promise<ItemResponse<GenericRes>> => {
    try {
      const response = await this.instance.put<
        GenericReq,
        AxiosWrapperType<ItemResponse<GenericRes>>
      >(this.endpoint, body, this.combineConfig(config));
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return error.response.data;
      }
      throw error;
    }
  };

  /**
   * PATCH request
   */
  patch = async (
    body?: GenericReq,
    config?: AxiosRequestConfig
  ): Promise<ItemResponse<GenericRes>> => {
    try {
      const response = await this.instance.patch<
        GenericReq,
        AxiosWrapperType<ItemResponse<GenericRes>>
      >(this.endpoint, body, this.combineConfig(config));
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return error.response.data;
      }
      throw error;
    }
  };

  /**
   * DELETE request
   */
  delete = async (
    config?: AxiosRequestConfig
  ): Promise<ItemResponse<GenericRes>> => {
    try {
      const response = await this.instance.delete<
        GenericReq,
        AxiosWrapperType<ItemResponse<GenericRes>>
      >(this.endpoint, this.combineConfig(config));
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return error.response.data;
      }
      throw error;
    }
  };
}

export default AxiosClient;
